import React from 'react';
import _ from 'lodash';

import {
  Chip,
  Link,
  Theme,
  styled
} from '@mui/material';
import {
  NetSuiteRecord,
  NetSuiteWriteRequest
} from 'generated/graphql';

import { routeDirectory } from 'modules/layer00/routeDirectory';



const StyledChip = styled(Chip, {
  shouldForwardProp: propName => propName !== 'writeStatusType'
})<{
  writeStatusType: IWriteStatusType;
}>(({
  theme,
  writeStatusType
}) => ({
  textTransform: 'uppercase',
  fontSize: 10,
  background: __writeStatus2FnBackgroundColor[writeStatusType](theme),
  color: __writeStatus2FnTextColor[writeStatusType](theme),
  fontWeight: 'bold',
  cursor: (writeStatusType === 'success' || writeStatusType === 'failure') ? 'pointer' : 'cursor'
}));



const __writeStatus2FnBackgroundColor: Record<IWriteStatusType, (theme: Theme) => string> = {
  failure: theme => theme.palette.error.light,
  pending: theme => theme.palette.primary.light,
  success: theme => theme.palette.success.light
};

const __writeStatus2FnTextColor: Record<IWriteStatusType, (theme: Theme) => string> = {
  failure: theme => theme.palette.error.main,
  pending: theme => theme.palette.primary.main,
  success: theme => theme.palette.success.main
};



export interface ISuccessfulWrite {
  type: 'success';
  urlToNetSuiteRecord: string;
  transactionId: string;
}

export interface IPendingWrite {
  type: 'pending';
}

export interface IWriteFailure {
  type: 'failure';
  urlToWriteFailure: string;
}

export type IWriteStatusType = IWriteStatus['type'];

export type IWriteStatus = ISuccessfulWrite | IPendingWrite | IWriteFailure;

export interface IProps {
  writeStatus: IWriteStatus;
}

export const FC: React.FC<IProps> = props => {
  const label = React.useMemo(
    () => {
      if (props.writeStatus.type === 'success' && props.writeStatus.transactionId) {
        return props.writeStatus.transactionId;
      }

      return __writeType2Label[props.writeStatus.type];
    },
    [props.writeStatus]
  );

  const child = (
    <StyledChip
      size="small"
      writeStatusType={props.writeStatus.type}
      label={label} />
  );

  if (props.writeStatus.type === 'success') {
    return (
      <Link
        href={props.writeStatus.urlToNetSuiteRecord}
        target="_blank">
        {child}
      </Link>
    );

  } else if (props.writeStatus.type === 'failure') {
    return (
      <Link
        href={props.writeStatus.urlToWriteFailure}
        target="_blank">
        {child}
      </Link>
    );

  } else {
    return child;
  }
};



const __writeType2Label: Record<IWriteStatusType, string> = {
  failure: 'Failed',
  pending: 'Pending',
  success: 'View'
};



export function genWriteStatus({
  netSuiteRecord, writeRequest
}: {
  netSuiteRecord?: Partial<NetSuiteRecord> | null;
  writeRequest?: Partial<NetSuiteWriteRequest> | null;
}): IWriteStatus {
  if (netSuiteRecord) {
    return {
      type: 'success',
      urlToNetSuiteRecord: netSuiteRecord.recordUrl ?? '',
      transactionId: netSuiteRecord.transactionId ?? ''
    };

  } else if (writeRequest && writeRequest.validationErrors && !_.isEmpty(writeRequest.validationErrors)) {
    return {
      type: 'failure',
      urlToWriteFailure: routeDirectory.admin.netSuite.path('validationErrors', writeRequest.id)
    };
  }

  return {
    type: 'pending'
  };
}
